// import stripe from 'tipsi-stripe';
import {
  CLEAR_NEXT_ACTION,
  CLEAR_PURCHASE_STATE,
  GET_LABEL_SUCCESS,
  LABEL_CREATE_SUCCESS,
  MPESA,
  PURCHASE_LABEL,
  PURCHASE_LABEL_ERROR,
  PURCHASE_LABEL_SUCCESS,
  PURCHASE_NEXT_ACTION,
  UPDATE_FILE_TYPE,
  UPDATE_PURCHASE_EMAIL,
} from "../../constants/types";
import { analytics } from "../../services/firebase";
import instance from "../../services/axiosConfig";
import { parseError } from "./ActionHelpers";

const pollCancellation = { cancelled: false };
const POLL_CANCELLED = "__poll_cancelled__";

export const updatePurchaseEmail = (email) => {
  return { type: UPDATE_PURCHASE_EMAIL, payload: email };
};

export const updateFileType = (type) => {
  return { type: UPDATE_FILE_TYPE, payload: type };
};

export const getRatePurchaseDetails = (id) => {
  return new Promise((resolve, reject) => {
    instance
      .get(`v1/shipping-labels/${id}`)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        console.log(err);
        reject(err);
      });
  });
};

const pollPaymentState = async (paymentId) => {
  return new Promise((resolve, reject) => {
    if (!paymentId) {
      reject("");
    }
    let status = null;

    const interval = setInterval(() => {
      if (status && status === "complete") {
        clearInterval(interval);
        resolve();
      } else if (!status || status === "pending") {
        instance
          .get(`payments/credits/${paymentId}`)
          .then((res) => {
            status = res.data?.state.toString().toLowerCase() || null;
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        reject("Transaction has been canceled");
        clearInterval(interval);
      }
    }, 2000);
  });
};

const pollLabelState = async (
  ratePurchase,
  paymentId,
  onNextAction,
  onFailed,
) => {
  return new Promise((resolve, reject) => {
    if (!ratePurchase) {
      reject("");
    }
    pollCancellation.cancelled = false;
    let lastNextActionType = null;

    const interval = setInterval(() => {
      if (pollCancellation.cancelled) {
        clearInterval(interval);
        reject(POLL_CANCELLED);
        return;
      }

      if (paymentId) {
        instance
          .get(`payments/credits/${paymentId}`)
          .then((paymentRes) => {
            const payment = paymentRes.data;
            const paymentState = payment?.state?.toString().toLowerCase();

            if (paymentState === "complete") {
              lastNextActionType = null;
              checkLabelState();
              return;
            }

            if (paymentState === "cancelled" || paymentState === "failed") {
              clearInterval(interval);
              reject("Payment was cancelled or failed");
              return;
            }

            if (payment?.next_action) {
              if (payment.next_action.type !== lastNextActionType) {
                lastNextActionType = payment.next_action.type;
                if (onNextAction) {
                  onNextAction({
                    ...payment.next_action,
                    payment_id: paymentId,
                  });
                }
              }
              return;
            }
          })
          .catch(() => {
            checkLabelState();
          });
      } else {
        checkLabelState();
      }

      function checkLabelState() {
        getRatePurchaseDetails(ratePurchase)
          .then((res) => {
            let status = res.data?.state.toString().toLowerCase() || null;

            if (status && status === "complete") {
              clearInterval(interval);
              resolve(res);
            }
            if ((status && status === "failed") || res.data.errors.length > 0) {
              clearInterval(interval);
              reject(parseError(res.data.errors));
              if (onFailed) onFailed();
            }
          })
          .catch((err) => {
            console.log(parseError(err));
          });
      }
    }, 2500);
  });
};

export const addCredits = async (card, rateAmount, currency) => {
  return new Promise(async (resolve, reject) => {
    const payload = {
      payment_method: card.id,
      via: {
        method: "existing_payment_method",
        payment_method_id: card.id,
      },
    };
    await instance
      .post(`/v1/wallets/${currency}/credits`, {
        desired_balance: rateAmount.toFixed(2),
        ...payload,
      })
      .then(async (res) => {
        await pollPaymentState(res.data.id).then(() => {
          resolve();
        });
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const createShippingLabel = async (rateRequest, via) => {
  const body = via ? { ...rateRequest, ...via } : rateRequest;
  return instance.post("v1/shipping-labels/?wait_for_purchase=false", body);
};

export const resumeLabelPurchase = (labelId, paymentId, method, onSuccess) => {
  return (dispatch) => {
    pollLabelState(
      labelId,
      paymentId,
      (nextAction) => {
        dispatch({ type: PURCHASE_NEXT_ACTION, payload: nextAction });
      },
      () => {
        dispatch(clearPurchaseStates());
      },
    )
      .then((res) => {
        dispatch({ type: CLEAR_NEXT_ACTION });
        purchaseRateSuccess(dispatch, res, method, onSuccess);
      })
      .catch((error) => {
        if (error === POLL_CANCELLED) return;
        const errMsg = parseError(error);
        dispatch({ type: PURCHASE_LABEL_ERROR, payload: errMsg });
      });
  };
};

const resolvePaymentResponse = (payment, paymentId, dispatch) => {
  const state = payment?.state?.toString().toLowerCase();
  if (payment?.next_action) {
    dispatch({
      type: PURCHASE_NEXT_ACTION,
      payload: { ...payment.next_action, payment_id: paymentId },
    });
  } else if (state === "complete") {
    dispatch({
      type: PURCHASE_NEXT_ACTION,
      payload: { type: "payment_complete", payment_id: paymentId },
    });
  } else if (state === "cancelled" || state === "failed") {
    dispatch({
      type: PURCHASE_NEXT_ACTION,
      payload: {
        type: "payment_failed",
        message: "Payment was cancelled or failed",
      },
    });
  } else {
    dispatch({ type: CLEAR_NEXT_ACTION });
  }
};

export const submitPaystackPin = (paymentId, pin) => {
  return async (dispatch) => {
    try {
      const res = await instance.post(
        `v1/payments/${paymentId}/paystack/submit-pin`,
        { pin },
      );
      resolvePaymentResponse(res.data, paymentId, dispatch);
    } catch (err) {
      pollCancellation.cancelled = true;
      dispatch({ type: PURCHASE_LABEL_ERROR, payload: parseError(err) });
    }
  };
};

export const submitPaystackOtp = (paymentId, otp) => {
  return async (dispatch) => {
    try {
      const res = await instance.post(
        `v1/payments/${paymentId}/paystack/submit-otp`,
        { otp },
      );
      resolvePaymentResponse(res.data, paymentId, dispatch);
    } catch (err) {
      pollCancellation.cancelled = true;
      dispatch({ type: PURCHASE_LABEL_ERROR, payload: parseError(err) });
    }
  };
};

export const purchaseRateCard = (
  { rate, email, promo, method, giftExchangeId, file_type, order_id, shipment },
  card,
  transaction,
  selectedWallet,
  onSuccess,
  onError,
) => {
  return async (dispatch) => {
    dispatch({ type: PURCHASE_LABEL });
    const rateRequest = { rate, email, file_type };
    if (order_id && order_id !== "") {
      rateRequest.order_id = order_id;
    }
    const rateAmount = rate.discounted_amount
      ? parseFloat(rate.discounted_amount)
      : parseFloat(rate.amount);
    const hasEnoughBalance =
      !!selectedWallet && parseFloat(selectedWallet.balance) >= rateAmount;
    const allowAnyBalance = !!selectedWallet?.allow_any_balance;

    try {
      const needsTopUp = !allowAnyBalance && !hasEnoughBalance;

      if (needsTopUp && !transaction) {
        if (!card?.is_default && !transaction) {
          dispatch({
            type: PURCHASE_LABEL_ERROR,
            payload: "Please make sure you have selected a payment method",
          });
          return;
        }

        const needsKES = card?.payment_method_type === MPESA;

        if (needsKES && rate.currency !== "KES") {
          dispatch({
            type: PURCHASE_LABEL_ERROR,
            payload: "MPESA payments can only be made for KES currency rates",
          });
          return;
        }

        if (needsKES) {
          await addCredits(card, rateAmount, rate.currency);
        }
      }

      const via = !transaction
        ? {
            via: {
              method: "existing_payment_method",
              payment_method_id: card?.id,
            },
          }
        : {
            via: {
              method: "paystack_token",
              token: transaction,
            },
          };

      const response = await createShippingLabel(
        rateRequest,
        needsTopUp ? via : null,
      );

      const labelId = response.data.id;
      const paymentId = response.data.payment_id || null;

      dispatch({ type: PURCHASE_LABEL_SUCCESS, payload: labelId });

      if (response.data.next_action) {
        dispatch({
          type: PURCHASE_NEXT_ACTION,
          payload: { ...response.data.next_action, payment_id: paymentId },
        });
      }

      dispatch(resumeLabelPurchase(labelId, paymentId, method, onSuccess));
    } catch (error) {
      console.error("Label purchase failed:", error);
      const errMsg = parseError(error);
      if (onError) onError();
      dispatch({
        type: PURCHASE_LABEL_ERROR,
        payload: errMsg,
      });
    }
  };
};

export const checkPendingRatePurchase =
  (id, method, onSuccess) => (dispatch) => {
    dispatch({ type: PURCHASE_LABEL });
    dispatch(resumeLabelPurchase(id, null, method, onSuccess));
  };

const purchaseRateSuccess = (dispatch, { data }, method, onSuccess) => {
  if (data != null) {
    dispatch({ type: GET_LABEL_SUCCESS, payload: data });
    dispatch({
      type: LABEL_CREATE_SUCCESS,
      payload: { promotion: data.promotion },
    });
    onSuccess(data);
  } else {
    const errors =
      "errors" in data
        ? data.errors
        : "Error occurred while processing your payment - please try again later";
    dispatch({
      type: PURCHASE_LABEL_ERROR,
      payload: errors,
    });
    analytics().logEvent(PURCHASE_LABEL_ERROR, { method });
  }
};

export const clearPurchaseStates = () => (dispatch) => {
  dispatch({
    type: CLEAR_PURCHASE_STATE,
  });
};
